import { z } from "zod";
import { StandardApiResponseSchema } from "../dbtDocs/types";
import { pageSchema } from "@/helpers/types";

export enum DatastoreTypeEnum {
  POSTGRES = "postgres",
  SNOWFLAKE = "snowflake",
  S3 = "s3",
  BIGQUERY = "bigquery",
  MYSQL = "mysql",
  TABLEAU = "tableau",
  DBT_CLOUD = "dbt_cloud",
  DBT_CORE = "dbt_core",
  DATABRICKS = "databricks",
}

export type DatastoreType = DatastoreTypeEnum;

export enum DatastoreInstanceEditionEnum {
  ENTERPRISE = "ENTERPRISE",
  STANDARD = "STANDARD",
  BUSINESS_CRITICAL = "BUSINESS_CRITICAL",
  NA = "NA",
}

export type InstanceEdition = DatastoreInstanceEditionEnum;

// This can be used directly in zod schemas
export const datastoreTypeEnum = [
  DatastoreTypeEnum.POSTGRES,
  DatastoreTypeEnum.SNOWFLAKE,
  DatastoreTypeEnum.S3,
  DatastoreTypeEnum.BIGQUERY,
  DatastoreTypeEnum.MYSQL,
  DatastoreTypeEnum.TABLEAU,
  DatastoreTypeEnum.DBT_CLOUD,
  DatastoreTypeEnum.DBT_CORE,
  DatastoreTypeEnum.DATABRICKS,
] as const;

// Zod schema for Datastore
export const DatastoreSchema = z.object({
  id: z.number(),
  name: z.string(),
  type: z.enum(datastoreTypeEnum),
  description: z.string().optional(),
  created_by: z.number().optional(),
  connection_info: z.string().optional(),
  last_modified_by: z.number().optional(),
  can_sync: z.boolean().optional(),
  auto_sync: z.boolean().optional(),
  last_synced_at: z.string().optional().nullable(),
  last_sync_status: z.string().optional().nullable(),
  instance_id: z.coerce.string().optional().nullable(),
  instance_edition: z.nativeEnum(DatastoreInstanceEditionEnum).optional().nullable(),
  is_organization_admin: z.boolean().optional().nullable(),
  // Per-connection label for Databricks, where every workspace connection
  // shares one account-level instance and ``name`` carries the instance
  // name instead. Null for every other datastore type.
  connection_name: z.string().optional().nullable(),
});

// Zod schema for DatastoreResponse
export const DatastoreResponseSchema = z.object({
  data_stores: z.array(DatastoreSchema),
});

// Infer types from schemas
export type Datastore = z.infer<typeof DatastoreSchema>;
export type DatastoreResponseInferred = z.infer<typeof DatastoreResponseSchema>;

export interface DatastoreTypeConfig {
  name: string;
  value: DatastoreType;
  formSteps: string[];
  enabled?: boolean;
}

export interface DatastoreFormData {
  name: string;
  type: DatastoreType;
  description: string;
  connection?: Record<string, unknown>;
  configuration?: Record<string, unknown>;
}

export interface DatastoreResponse {
  data_stores: Datastore[];
}

export const DatastoreTypeResponseSchema = z.array(
  z.object({
    value: z.enum(datastoreTypeEnum),
    enabled: z.boolean(),
    is_standalone: z.boolean(),
  })
);
export type DatastoreTypeResponse = z.infer<typeof DatastoreTypeResponseSchema>;

export enum SnowflakeConnectionType {
  PASSWORD = "password",
  KEY_PAIR = "key_pair",
}

export enum SnowflakePrivateKeyType {
  PEM = "pem",
  DER = "der",
}

export interface SnowflakeAuthKeyPair {
  private_key: string;
  passphrase: string;
  type: SnowflakePrivateKeyType;
}

export interface SnowflakeBasePayload {
  account: string;
  user: string;
  password: string;
  role?: string;
  warehouse?: string;
  name: string;
  type: string;
  description: string;
  key_pair: SnowflakeAuthKeyPair;
  auth_type: SnowflakeConnectionType;
}

// Test connection response schema
export const TestConnectionResponseSchema = z.object({
  ok: z.boolean(),
  message: z.string().nullish(),
});
export type TestConnectionResponse = z.infer<typeof TestConnectionResponseSchema>;

// Sync datastore response schema
export const SyncDatastoreResponseSchema = StandardApiResponseSchema;
export type SyncDatastoreResponse = z.infer<typeof SyncDatastoreResponseSchema>;

// Toggle sync schedule response schema
export const ToggleSyncScheduleResponseSchema = z.object({
  ok: z.boolean(),
});
export type ToggleSyncScheduleResponse = z.infer<typeof ToggleSyncScheduleResponseSchema>;

// Base datastore request schema (common fields across all datastore types)
// Inferred from usage in DatastoreAdd components
const BaseDatastoreRequestSchema = z.object({
  name: z.string(),
  type: z.string(),
  description: z.string(),
});

// Postgres datastore request schema
// Inferred from PostgresConnection.tsx line 92-99
export const PostgresDatastoreRequestSchema = BaseDatastoreRequestSchema.extend({
  host: z.string(),
  port: z.string(),
  databases: z.array(z.string()),
  database: z.string().optional(),
  user: z.string(),
  password: z.string(),
});
export type PostgresDatastoreRequest = z.infer<typeof PostgresDatastoreRequestSchema>;

export const ReaderAccountInputSchema = z.object({
  provider_account: z.string().trim().min(1).max(255),
  provider_organization: z.string().trim().min(1).max(255).optional(),
  compute_price: z.number().finite().positive().optional(),
  ai_price: z.number().finite().positive().optional(),
  storage_price: z.number().finite().positive().optional(),
});
export type ReaderAccountInput = z.infer<typeof ReaderAccountInputSchema>;

// Base Snowflake fields shared between both auth types
const SnowflakeBaseSchema = BaseDatastoreRequestSchema.extend({
  account: z.string(),
  user: z.string(),
  role: z.string().optional(),
  warehouse: z.string().optional(),
  databases: z.array(z.string()).optional(),
  reader_account: ReaderAccountInputSchema.optional(),
});

// Snowflake auth variants - defined once and reused
const SnowflakePasswordVariant = SnowflakeBaseSchema.extend({
  auth_type: z.literal(SnowflakeConnectionType.PASSWORD),
  password: z.string(),
});

const SnowflakeKeyPairVariant = SnowflakeBaseSchema.extend({
  auth_type: z.literal(SnowflakeConnectionType.KEY_PAIR),
  // Always sent, always empty. The backend builds the Snowflake connection
  // string with ``quote_plus(password)``, which raises on a missing value —
  // key-pair auth supplies credentials via ``key_pair`` instead (AI-8716).
  password: z.literal(""),
  key_pair: z.object({
    private_key: z.string(),
    passphrase: z.string(),
    type: z.nativeEnum(SnowflakePrivateKeyType),
  }),
});

// Snowflake schema with discriminated union for auth_type
export const SnowflakeDatastoreRequestSchema = z.discriminatedUnion("auth_type", [
  SnowflakePasswordVariant,
  SnowflakeKeyPairVariant,
]);
export type SnowflakeDatastoreRequest = z.infer<typeof SnowflakeDatastoreRequestSchema>;

// Tableau datastore request schema
// Inferred from TableauConnection.tsx line 130-140
export const TableauDatastoreRequestSchema = BaseDatastoreRequestSchema.extend({
  url: z.string(),
  host: z.string(),
  site_name: z.string(),
  // PAT becomes optional for the "Add database" modal on the detail page
  // when the user keeps the existing PAT (toggle OFF) — backend then
  // looks the credentials up via ``datastore_id``.
  pat_name: z.string().optional(),
  pat_secret: z.string().optional(),
  user: z.string().optional(),
  password: z.string().optional(),
  databases: z.array(z.string()),
  worksheet_location: z.string().optional(),
  datastore_id: z.number().optional(),
  // Sibling site name (in the same instance as ``datastore_id``) whose PAT
  // should be reused. Lets the picker pass a name instead of a sibling row
  // ID — the backend resolves the sibling via instance_id + site_name.
  source_site_name: z.string().optional(),
  // Attaches this new server URL to an existing connection instead of
  // creating a fresh instance. Sent by the detail page's "Add server URL"
  // flow; absent for the normal create-a-connection wizard.
  instance_id: z.number().optional(),
  connection: z
    .object({
      projects: z.array(z.string()).optional(),
    })
    .optional(),
});
export type TableauDatastoreRequest = z.infer<typeof TableauDatastoreRequestSchema>;

// Union type for all datastore requests
// This covers all possible datastore creation payloads
export const AddDatastoreRequestSchema = z.union([
  PostgresDatastoreRequestSchema,
  SnowflakeDatastoreRequestSchema,
  TableauDatastoreRequestSchema,
]);
export type AddDatastoreRequest = z.infer<typeof AddDatastoreRequestSchema>;

// Request for adding databases to an existing datastore.
// The Tableau Cloud "Add database" modal extends the base payload with
// optional per-site PAT (toggle ON case) and a chosen worksheet_location.
export type AddDatabasesRequest = Datastore & {
  databases: string[];
  pat_name?: string;
  pat_secret?: string;
  user?: string;
  password?: string;
  site_name?: string;
  worksheet_location?: string;
  url?: string;
  host?: string;
  // Pinpoints the existing sibling data_store whose PAT should be reused
  // for this new site. Optional — backend falls back to instance-level
  // lookup when absent.
  datastore_id?: number;
  // Site name of the sibling whose PAT should be reused. Sent together with
  // ``datastore_id`` (page context) so the backend resolves the sibling via
  // instance_id + site_name without the frontend needing sibling row IDs.
  source_site_name?: string;
};

// Add datastore response schema
// Inferred from usage - API returns success response
export const AddDatastoreResponseSchema = z.object({
  ok: z.boolean().nullish(),
  message: z.string().nullish(),
});
export type AddDatastoreResponse = z.infer<typeof AddDatastoreResponseSchema>;

// Test datastore connection during creation request schema
// Inferred from usage in PostgresConnection, SnowflakeConnection, TableauConnection
// This is similar to AddDatastoreRequest but used for testing connection before creation

// Snowflake test connection schema - reuses variants with database field added
const SnowflakeTestConnectionSchema = z.discriminatedUnion("auth_type", [
  SnowflakePasswordVariant.extend({ database: z.string().optional() }),
  SnowflakeKeyPairVariant.extend({ database: z.string().optional() }),
]);

export const TestDatastoreConnectionDuringCreationRequestSchema = z.union([
  PostgresDatastoreRequestSchema.extend({
    database: z.string().optional(),
  }),
  SnowflakeTestConnectionSchema,
  TableauDatastoreRequestSchema,
]);
export type TestDatastoreConnectionDuringCreationRequest = z.infer<
  typeof TestDatastoreConnectionDuringCreationRequestSchema
>;

// Test datastore connection during creation response schema
// Inferred from usage in TableauConnection.tsx: response.ok is checked
// Tableau backends additionally return `site_role` and `can_publish_workbooks`
// so the Step 3 workbook UI can adapt for SiteAdministratorExplorer PATs.
export const TestDatastoreConnectionDuringCreationResponseSchema = z
  .object({
    ok: z.boolean().nullable(),
    message: z.string().nullish(),
    site_role: z.string().nullish(),
    can_publish_workbooks: z.boolean().nullish(),
  })
  .passthrough();
export type TestDatastoreConnectionDuringCreationResponse = z.infer<
  typeof TestDatastoreConnectionDuringCreationResponseSchema
>;

// Available databases response schema
export const AvailableDatabasesResponseSchema = z.object({
  used_databases: z.array(z.string()),
  unused_databases: z.array(z.string()),
});
export type AvailableDatabasesResponse = z.infer<typeof AvailableDatabasesResponseSchema>;

// Router state handed to /settings/datastores/add so the connection form opens
// pre-bound to an existing connection. Used by the Tableau detail page's
// "Add server URL" action: the new host attaches to ``attach_instance_id``
// instead of creating a fresh instance.
export interface DatastoreAddPrefillState {
  attach_instance_id: number;
  name: string;
  description: string;
  type: DatastoreType;
}

/**
 * Router state is not ours alone — any navigation into the add page can carry
 * a state object, and an unrelated one must not put the form into the attach
 * flow with an undefined type and a half-filled payload. Only a state that
 * carries every field the flow reads counts as prefill; anything else falls
 * back to the normal create-a-connection wizard.
 */
export const isDatastoreAddPrefillState = (value: unknown): value is DatastoreAddPrefillState => {
  if (!value || typeof value !== "object") return false;
  const state = value as Partial<DatastoreAddPrefillState>;
  return (
    typeof state.attach_instance_id === "number" &&
    Number.isFinite(state.attach_instance_id) &&
    datastoreTypeEnum.includes(state.type as DatastoreTypeEnum) &&
    typeof state.name === "string" &&
    typeof state.description === "string"
  );
};

// One Tableau server URL of a connection. A connection can span several of
// them, each with its own ``data_stores`` row and its own sites, so the host
// is what tells two entries of the same connection apart.
export const DatastoreHostSchema = z.object({
  host: z.string(),
  data_store_id: z.number(),
  sites: z.array(z.string()),
});
export type DatastoreHost = z.infer<typeof DatastoreHostSchema>;

// Datastore detail response schema (single datastore). The connections list
// returns one row per connection and says nothing about server URLs; the
// detail response is the only place they are enumerated. Null for every
// non-Tableau type, and absent on backends predating multi-host support.
export const DatastoreDetailResponseSchema = DatastoreSchema.extend({
  hosts: z.array(DatastoreHostSchema).optional().nullable(),
});
export type DatastoreDetailResponse = z.infer<typeof DatastoreDetailResponseSchema>;

// Sync history item schema
export const SyncHistoryItemSchema = z.object({
  id: z.number(),
  event_id: z.number(),
  sync_status: z.string(),
  sync_event_type: z.string(),
  created_on: z.string(),
  // Tableau site this sync ran against; null for non-Tableau connections.
  site_name: z.string().nullish(),
});

export type SyncHistoryItem = z.infer<typeof SyncHistoryItemSchema>;

// Get sync history params
export interface GetSyncHistoryParams {
  page?: number;
  size?: number;
  start_time?: string;
  end_time?: string;
}

// Get sync history response schema
export const GetSyncHistoryResponseSchema = pageSchema(SyncHistoryItemSchema);

export type GetSyncHistoryResponse = z.infer<typeof GetSyncHistoryResponseSchema>;

// ============================================================================
// dbt Cloud Integration Types
// ============================================================================

export enum SyncType {
  SCHEDULED = "scheduled",
  REAL_TIME = "real_time",
}

// Response schemas for dbt Cloud API

export const DbtCloudScheduleResponseSchema = z.object({
  id: z.number(),
  sync_type: z.string(),
  cron_expr: z.string(),
  lookback_days: z.number(),
  is_active: z.boolean(),
  last_sync_at: z.string().nullable().optional(),
  last_sync_status: z.string().nullable().optional(),
  last_sync_error: z.string().nullable().optional(),
  created_at: z.string(),
  last_modified_at: z.string(),
});
export type DbtCloudScheduleResponse = z.infer<typeof DbtCloudScheduleResponseSchema>;

export const DbtCloudSyncDetailsSchema = z.object({
  synced_files: z.array(z.string()).optional().default([]),
  failed_files: z
    .array(z.object({ name: z.string(), error: z.string() }))
    .optional()
    .default([]),
  missing_artifacts: z.array(z.string()).optional().default([]),
  errors: z.array(z.string()).optional().default([]),
  ingestion_error: z.string().nullable().optional(),
});
export type DbtCloudSyncDetails = z.infer<typeof DbtCloudSyncDetailsSchema>;

export const DbtCloudProcessedRunResponseSchema = z.object({
  id: z.number(),
  dbt_cloud_run_id: z.string(),
  project_name: z.string().nullable().optional(),
  environment_name: z.string().nullable().optional(),
  run_finished_at: z.string(),
  processed_at: z.string(),
  artifacts_downloaded: z.array(z.string()),
  status: z.string(),
  error_message: z.string().nullable().optional(),
  sync_details: DbtCloudSyncDetailsSchema.nullable().optional(),
});
export type DbtCloudProcessedRunResponse = z.infer<typeof DbtCloudProcessedRunResponseSchema>;

export const DbtCloudEnvironmentResponseSchema = z.object({
  id: z.number(),
  external_environment_id: z.string(),
  environment_name: z.string().nullable().optional(),
  environment_type: z.string().nullable().optional(),
  dbt_core_integration_environment_id: z.number(),
  is_enabled: z.boolean(),
  last_processed_run_finished_at: z.string().nullable().optional(),
  created_at: z.string(),
  last_modified_at: z.string(),
  processed_runs_count: z.number().nullable().optional(),
});
export type DbtCloudEnvironmentResponse = z.infer<typeof DbtCloudEnvironmentResponseSchema>;

export const DbtCloudProjectResponseSchema = z.object({
  id: z.number(),
  external_project_id: z.string(),
  project_name: z.string().nullable().optional(),
  dbt_core_integration_id: z.number(),
  is_enabled: z.boolean(),
  created_at: z.string(),
  last_modified_at: z.string(),
  environments: z.array(DbtCloudEnvironmentResponseSchema).nullable().optional(),
});
export type DbtCloudProjectResponse = z.infer<typeof DbtCloudProjectResponseSchema>;

export const DbtCloudConnectionResponseSchema = z.object({
  id: z.number(),
  name: z.string(),
  account_id: z.string(),
  api_base_url: z.string(),
  is_active: z.boolean(),
  created_at: z.string(),
  last_modified_at: z.string(),
  created_by: z.number(),
  last_modified_by: z.number(),
  projects: z.array(DbtCloudProjectResponseSchema).nullable().optional(),
  schedule: DbtCloudScheduleResponseSchema.nullable().optional(),
});
export type DbtCloudConnectionResponse = z.infer<typeof DbtCloudConnectionResponseSchema>;

export const DbtCloudConnectionListResponseSchema = z.object({
  connections: z.array(DbtCloudConnectionResponseSchema),
});
export type DbtCloudConnectionListResponse = z.infer<typeof DbtCloudConnectionListResponseSchema>;

export const DbtCloudSyncResponseSchema = z.object({
  sync_triggered: z.boolean(),
  task_id: z.string().nullable().optional(),
  projects_queued: z.number(),
  environments_queued: z.number(),
  message: z.string().nullable().optional(),
});
export type DbtCloudSyncResponse = z.infer<typeof DbtCloudSyncResponseSchema>;

export const DbtCloudSyncHistoryResponseSchema = z.object({
  connection_id: z.number(),
  environments: z.array(DbtCloudEnvironmentResponseSchema),
  recent_runs: z.array(DbtCloudProcessedRunResponseSchema),
  // Pagination fields
  page: z.number(),
  pages: z.number(),
  size: z.number(),
  total: z.number(),
});
export type DbtCloudSyncHistoryResponse = z.infer<typeof DbtCloudSyncHistoryResponseSchema>;

export const DbtCloudDiscoveryResponseSchema = z.object({
  connection_id: z.number(),
  projects_discovered: z.number(),
  environments_discovered: z.number(),
  projects: z.array(DbtCloudProjectResponseSchema),
});
export type DbtCloudDiscoveryResponse = z.infer<typeof DbtCloudDiscoveryResponseSchema>;

// Discovered schemas (for validation response - not yet persisted to DB)
export const DbtCloudDiscoveredEnvironmentSchema = z.object({
  id: z.string(),
  name: z.string().nullable().optional(),
  environment_type: z.string().nullable().optional(),
});
export type DbtCloudDiscoveredEnvironment = z.infer<typeof DbtCloudDiscoveredEnvironmentSchema>;

export const DbtCloudDiscoveredProjectSchema = z.object({
  id: z.string(),
  name: z.string().nullable().optional(),
  environments: z.array(DbtCloudDiscoveredEnvironmentSchema).optional().default([]),
});
export type DbtCloudDiscoveredProject = z.infer<typeof DbtCloudDiscoveredProjectSchema>;

export const DbtCloudValidationResponseSchema = z.object({
  valid: z.boolean(),
  account_id: z.string().nullable().optional(),
  account_name: z.string().nullable().optional(),
  message: z.string().nullable().optional(),
  // Discovery data (only populated when valid=true)
  projects_discovered: z.number().nullish(),
  environments_discovered: z.number().nullish(),
  projects: z.array(DbtCloudDiscoveredProjectSchema).nullish(),
});
export type DbtCloudValidationResponse = z.infer<typeof DbtCloudValidationResponseSchema>;

// Conflict error schemas (returned when connection creation fails due to duplicate projects)
export const DbtCloudConflictingProjectSchema = z.object({
  project_name: z.string(),
  external_project_id: z.string(),
});
export type DbtCloudConflictingProject = z.infer<typeof DbtCloudConflictingProjectSchema>;

export const DbtCloudConflictErrorSchema = z.object({
  error_code: z.literal("CONFLICT_ERROR"),
  message: z.string(),
  conflicting_projects: z.array(DbtCloudConflictingProjectSchema),
});
export type DbtCloudConflictError = z.infer<typeof DbtCloudConflictErrorSchema>;

// Request schemas for dbt Cloud

export const DbtCloudConnectionCreateRequestSchema = z.object({
  name: z.string().min(1, "Name is required").max(128, "Name must be 128 characters or less"),
  api_token: z.string().min(1, "API token is required"),
  account_id: z
    .string()
    .min(1, "Account ID is required")
    .max(64, "Account ID must be 64 characters or less"),
  api_base_url: z.string().optional().default("https://cloud.getdbt.com/api/v2/"),
  sync_type: z.enum(["scheduled", "real_time"]).optional().default("scheduled"),
  schedule_cron: z.string().optional().default("0 0 * * *"),
  lookback_days: z.number().min(1).max(365).optional().default(30),
});
export type DbtCloudConnectionCreateRequest = z.infer<typeof DbtCloudConnectionCreateRequestSchema>;

export const DbtCloudProjectUpdateRequestSchema = z.object({
  is_enabled: z.boolean(),
});
export type DbtCloudProjectUpdateRequest = z.infer<typeof DbtCloudProjectUpdateRequestSchema>;

export const DbtCloudEnvironmentUpdateRequestSchema = z.object({
  is_enabled: z.boolean(),
});
export type DbtCloudEnvironmentUpdateRequest = z.infer<
  typeof DbtCloudEnvironmentUpdateRequestSchema
>;

export const DbtCloudScheduleUpdateRequestSchema = z.object({
  sync_type: z.enum(["scheduled", "real_time"]).optional(),
  cron_expr: z.string().optional(),
  lookback_days: z.number().min(1).max(365).optional(),
  is_active: z.boolean().optional(),
});
export type DbtCloudScheduleUpdateRequest = z.infer<typeof DbtCloudScheduleUpdateRequestSchema>;

export const DbtCloudConnectionUpdateRequestSchema = z.object({
  name: z.string().min(1).max(128).optional(),
  api_token: z.string().min(1).optional(),
  api_base_url: z.string().optional(),
});
export type DbtCloudConnectionUpdateRequest = z.infer<typeof DbtCloudConnectionUpdateRequestSchema>;

export const DbtCloudValidateTokenRequestSchema = z.object({
  api_token: z.string().min(1, "API token is required"),
  account_id: z.string().min(1, "Account ID is required"),
  api_base_url: z.string().optional(),
});
export type DbtCloudValidateTokenRequest = z.infer<typeof DbtCloudValidateTokenRequestSchema>;

// Query keys for dbt Cloud
export const DBT_CLOUD_QUERY_KEYS = {
  CONNECTIONS: "dbt-cloud-connections",
  CONNECTION: "dbt-cloud-connection",
  SYNC_HISTORY: "dbt-cloud-sync-history",
  VALIDATE_TOKEN: "dbt-cloud-validate-token",
} as const;

// Query keys for dbt Core (migrated from integrations/constants.ts)
export const GET_INTEGRATIONS = "integrations";

// ============================================================================
// dbt Core Integration Types
// ============================================================================

export const DbtCoreIntegrationCreateRequestSchema = z.object({
  name: z
    .string({ message: "Integration name is required" })
    .min(3, { message: "Integration name must be at least 3 characters long" }),
  integration_type: z.enum(["dbt_core"], { message: "Integration type is required" }),
  environment: z
    .string({ message: "Environment name is required" })
    .min(3, { message: "Environment name must be at least 3 characters long" }),
});
export type DbtCoreIntegrationCreateRequest = z.infer<typeof DbtCoreIntegrationCreateRequestSchema>;
